import { useMutation, useQueryClient } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';

import type { ConversationAnalysisResponse, ConversationAnalysis } from './types';

export interface AnalyzeConversationParams {
  artistId: string;
  conversationId: string;
  force?: boolean;
}

export async function analyzeConversation({
  artistId,
  conversationId,
  force = false,
}: AnalyzeConversationParams): Promise<ConversationAnalysis> {
  if (!artistId) throw Error('Artist ID is required');
  if (!conversationId) throw Error('Conversation ID is required');

  const url = `/web/studio/artists/${artistId}/conversations/${conversationId}/analyze`;
  const response = await api.executePostRequest<ConversationAnalysisResponse>(url, { force });

  return response.data.data;
}

export function useAnalyzeConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: analyzeConversation,
    onSuccess: (data, variables) => {
      // Invalidate the conversations list to refresh with new analysis data
      queryClient.invalidateQueries({
        queryKey: queryKeys.artistChatbotConversations(variables.artistId, {}),
      });
      // Also invalidate the specific conversation
      queryClient.invalidateQueries({
        queryKey: queryKeys.artistChatbotConversation(variables.artistId, variables.conversationId),
      });
      // Invalidate stats as well
      queryClient.invalidateQueries({
        queryKey: queryKeys.artistConversationStats(variables.artistId, {}),
      });
    },
  });
}
